import React, { useEffect, useState } from 'react';
import { Modal, Spinner, Table } from 'react-bootstrap';
import moment from 'moment';
import { ActiveUsersGroup, IActiveUser } from '../dto/iStatistic';
import { StatisticsService } from '../StatisticsService';
import { ErrorToast } from '../../../utils/toasters';
import { Pagination } from '../../../shared/components/Pagination';
import Flag from '../../../shared/components/Flag';

export interface IActiveUsersSelection {
    title: string;
    minutes: number;
    group?: ActiveUsersGroup;
}

interface IStatisticsActiveUsersModalProps {
    selection?: IActiveUsersSelection;
    onClose: () => void;
}

const PER_PAGE = 15;

const formatDateTime = (date?: string) => (date ? moment(date).format('DD.MM.YYYY. HH:mm:ss') : '-');

export function StatisticsActiveUsersModal({ selection, onClose }: IStatisticsActiveUsersModalProps) {
    const [users, setUsers] = useState<IActiveUser[]>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        setPage(1);
    }, [selection?.title, selection?.minutes, selection?.group]);

    useEffect(() => {
        if (!selection) {
            return;
        }
        setIsLoading(true);
        StatisticsService.getActiveUsersList({
            minutes: selection.minutes,
            group: selection.group,
            page,
            perPage: PER_PAGE,
        })
            .then(response => {
                setUsers(response.data);
                setCount(response.count);
            })
            .catch(err => ErrorToast(err))
            .finally(() => setIsLoading(false));
    }, [selection?.title, selection?.minutes, selection?.group, page]);

    return (
        <Modal show={!!selection} onHide={onClose} size="xl" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {selection?.title} <span className="text-muted fs-6">({count} users)</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading && !users.length ? (
                    <div className="text-center py-10">
                        <Spinner animation="border" />
                    </div>
                ) : (
                    <Table hover responsive className={isLoading ? 'opacity-50' : ''}>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Type</th>
                                <th>Company</th>
                                <th>Country</th>
                                <th>Last active</th>
                                <th>Last login</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user.id}>
                                    <td>{user.id}</td>
                                    <td>
                                        {user.displayName?.trim() ||
                                            `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() ||
                                            '-'}
                                    </td>
                                    <td>{user.email}</td>
                                    <td>{user.userType}</td>
                                    <td>{user.brandCompany?.displayName ?? user.agencyCompany?.displayName ?? '-'}</td>
                                    <td>{user.contact?.country ? <Flag code={user.contact.country} /> : '-'}</td>
                                    <td>{formatDateTime(user.lastActiveAt)}</td>
                                    <td>{formatDateTime(user.lastLogin)}</td>
                                </tr>
                            ))}
                            {!isLoading && !users.length && (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted py-6">
                                        No users in this period.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                )}
                <Pagination
                    page={page}
                    totalPages={Math.ceil(count / PER_PAGE)}
                    handlePagination={setPage}
                    disabled={isLoading}
                />
            </Modal.Body>
        </Modal>
    );
}
